/* lexAgora — JS facade for the native Agora bridge (iOS + Android).

   On native builds the actual Agora engine runs in Swift/Java code via
   the LexAgora Capacitor plugin (ios/App/App/LexAgoraPlugin.swift +
   android/app/src/main/java/vip/lexiq/app/LexAgoraPlugin.java).  That
   gives us real OS-level audio routing so 1-to-1 voice calls can use
   the EARPIECE by default — same behavior as WhatsApp / Telegram.

   On the PWA / desktop builds the native plugin is absent; callers
   should fall back to the existing `agora-rtc-sdk-ng` Web SDK there.
   Use the `hasNativeAgora` flag exported below to branch.

   Wraps every method in a try/catch so callers can `await` without
   worrying about hard rejections crashing the call flow.
*/
import { Capacitor, registerPlugin } from "@capacitor/core";

const LexAgora = registerPlugin("LexAgora");

export const hasNativeAgora = Capacitor?.isNativePlatform?.() === true;

if (typeof console !== "undefined") {
  // eslint-disable-next-line no-console
  console.log(
    "[LexAgora] facade init — hasNativeAgora=",
    hasNativeAgora,
    "platform=",
    Capacitor.getPlatform?.(),
  );
}

const safe = (fn) => async (...args) => {
  if (!hasNativeAgora) return { ok: false, native: false };
  try {
    const res = await fn(...args);
    return { ok: true, native: true, ...(res || {}) };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[LexAgora] native call failed:", e);
    return { ok: false, native: true, error: String(e?.message || e) };
  }
};

export const initialize  = safe(({ appId })                  => LexAgora.initialize({ appId }));
export const joinChannel = safe(({ token, channel, uid })    => LexAgora.joinChannel({ token, channel, uid }));
export const leaveChannel = safe(()                          => LexAgora.leaveChannel());
export const setMute     = safe((mute)                       => LexAgora.setMute({ mute: !!mute }));
export const setSpeaker  = safe((on)                         => LexAgora.setSpeaker({ on: !!on }));
export const currentRoute = safe(()                          => LexAgora.currentRoute());

/** Subscribe to a native event. Returns an unsubscribe function. */
export function addListener(event, handler) {
  if (!hasNativeAgora) return () => {};
  const sub = LexAgora.addListener(event, handler);
  return () => {
    try {
      sub.then?.((h) => h.remove?.());
      sub.remove?.();
    } catch { /* noop */ }
  };
}
